import { useEffect, useState } from 'react';

import { ApiError } from './api';

/**
 * One async read, with the three states a page actually has.
 *
 * ## Why not just `useEffect` in each page
 *
 * Because every page would then get the abort handling slightly differently,
 * and the one that got it wrong would show the previous query's results after
 * the customer typed a new one. The request is aborted when the key changes, so
 * a slow first response cannot overwrite a fast second.
 *
 * `status` is explicit rather than derived from `data === undefined`: "loading"
 * and "loaded nothing" are different things to a customer, and collapsing them
 * is how an empty marketplace ends up showing a permanent spinner.
 */

export type ResourceState<T> =
  | { readonly status: 'loading' }
  | { readonly status: 'loaded'; readonly data: T }
  | { readonly status: 'error'; readonly message: string; readonly notFound: boolean };

export function useResource<T>(
  key: string,
  load: (signal: AbortSignal) => Promise<T>,
): ResourceState<T> & { readonly reload: () => void } {
  const [state, setState] = useState<ResourceState<T>>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    setState({ status: 'loading' });

    load(controller.signal)
      .then((data) => {
        if (live) setState({ status: 'loaded', data });
      })
      .catch((error: unknown) => {
        if (!live) return;
        if (error instanceof Error && error.name === 'AbortError') return;

        // 404 is a different page, not a different message. A mistyped offer id
        // should render "not found", and an unreachable API should render a
        // retry — conflating them sends people to the wrong recovery.
        const notFound = error instanceof ApiError && error.status === 404;

        setState({
          status: 'error',
          message: error instanceof ApiError ? error.message : 'Something went wrong. Please try again.',
          notFound,
        });
      });

    return () => {
      live = false;
      controller.abort();
    };
    // `key` is the whole query, and `attempt` is the retry counter. `load` is
    // deliberately NOT a dependency: every caller passes an inline closure, so
    // including it would re-run the effect on every render and turn one search
    // into an infinite request loop. The key is what identifies the request.
  }, [key, attempt]);

  return { ...state, reload: () => setAttempt((value) => value + 1) };
}
